import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { generatePath, useLocation, useNavigate, useRevalidator } from 'react-router';
import { EcommerceEventTypeV4, pushSubscriptionModifyEventToDataLayer } from '../../common/analytics';
import { paths } from '../../common/constants/pathVariables';
import { AddOnVisibilityOperationType, DialogType, SubscriptionAddonCode } from '../../common/enums';
import { changeSubscriptionFeatures } from '../../common/fetch';
import { useAddOnVisibility } from '../../common/hooks/useAddOnVisibility';
import { useSearchParams } from '../../common/hooks/useSearchParams';
import { somethingWentWrongMsg, startTimeCantBeGreaterMsg, t } from '../../common/i18n';
import { getSubscriptionUrlId } from '../../common/utils/subscriptionUtils';
import { SubscriptionStatusType } from '../../generated/api/subscriptionStatusType';
import { openDialog } from '../../public/site/dialogStore.js';
import { addNotification } from '../../public/site/notificationStore.js';
import {
  getReachabilityAddonAttributes,
  getReachabilityWithMobileFormValues,
  getReachabilityWithMobilePayload,
  pushAnalyticsEvent,
  workingHourErrors,
} from '../PbxAccordionContent/PbxAccordionUtils';
import { useAnalyticsId } from '../SubscriptionAdditionalService/addOnAnalyticsUtils';
import { getAddOnsByCodes } from '../SubscriptionDetails/addOnDependencyUtils';
import { AddReachabilityWithMobileAddon } from './AddReachabilityWithMobileAddon';
import { ReachabilityWithMobileEdit } from './ReachabilityWithMobileEdit';
import { ReachabilityWithMobileView } from './ReachabilityWithMobileView';
import type { DialogParams } from '../../common/types/dialog';
import type { Subscription } from '../../generated/api/subscription';
import type {
  AssociationRecord,
  DependencyRecord,
} from '../../legacy/cloudsense-add-on-dependency-engine/AddOnManagerOnline';
import type { ReachabilityWithMobileFormValues } from '../PbxAccordionContent/PbxAccordionUtils';

import './PbxAdditionalServices.scss';

interface ReachabilityWithMobileAccordionProps {
  hasPendingActions: boolean;
  pbxSolution: Subscription;
  subscription: Subscription;
  addOnRulesAssociations?: AssociationRecord[];
  addOnRulesDependencies?: DependencyRecord[];
  isEditing: boolean;
  setEditing: (isEditing: boolean) => void;
  whiteButton?: boolean;
}

export const ReachabilityWithMobileAccordion = ({
  subscription,
  hasPendingActions,
  addOnRulesAssociations,
  addOnRulesDependencies,
  isEditing,
  setEditing,
  whiteButton = false,
}: ReachabilityWithMobileAccordionProps) => {
  const { mdmId } = useSearchParams<{ mdmId?: string }>();
  const [isSaving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { search } = useLocation();
  const analyticsBundleId = useAnalyticsId();
  const { addOnVisibilities } = useAddOnVisibility(
    subscription.commercialProductCode || '',
    AddOnVisibilityOperationType.CHANGE,
    undefined,
    mdmId
  );
  const { revalidate } = useRevalidator();
  const onShowDialog = (params: DialogParams) => openDialog(params);

  const reachabilityAddon = subscription.details?.selectedAddOns?.find(
    addon => addon.addOnCode === SubscriptionAddonCode.REACHABILITY_WITH_MOBILE
  );
  const addonAttributes = reachabilityAddon ? getReachabilityWithMobileFormValues(reachabilityAddon) : {};

  const methods = useForm({
    defaultValues: addonAttributes,
    mode: 'onSubmit',
  });

  if (!reachabilityAddon) {
    return (
      <AddReachabilityWithMobileAddon
        hasPendingActions={hasPendingActions}
        subscription={subscription}
        addOnRulesAssociations={addOnRulesAssociations}
        addOnRulesDependencies={addOnRulesDependencies}
        addOnVisibilities={addOnVisibilities}
        analyticsBundleId={analyticsBundleId}
        whiteButton={whiteButton}
      />
    );
  }

  const onEdit = () => {
    if (subscription?.subscriptionStatus === SubscriptionStatusType.IN_ACTIVATION) {
      onShowDialog({ type: DialogType.MACD_FORBIDDEN_SUBSCRIPTION_IN_ACTIVATION });
      return;
    }
    if (hasPendingActions) {
      onShowDialog({ type: DialogType.SUBSCRIPTION_ACTION_PENDING });
      return;
    }
    setEditing(true);
  };

  const onConfirm = async (values: ReachabilityWithMobileFormValues) => {
    pushSubscriptionModifyEventToDataLayer('reachability settings');
    const weekdayErrors = workingHourErrors(values?.workingHoursWeekdayStart, values.workingHoursWeekdayEnd);
    const weekEndErrors = workingHourErrors(values?.workingHoursWeekendStart, values.workingHoursWeekendEnd);
    if (weekdayErrors || weekEndErrors) {
      if (weekdayErrors) {
        methods.setError('workingHoursWeekdayEnd', { message: t.E9GS(startTimeCantBeGreaterMsg) });
      }
      if (weekEndErrors) {
        methods.setError('workingHoursWeekendEnd', { message: t.E9GS(startTimeCantBeGreaterMsg) });
      }
      return;
    }
    setSaving(true);

    const ChangesAddOns = getAddOnsByCodes(
      [SubscriptionAddonCode.REACHABILITY_WITH_MOBILE],
      addOnVisibilities || [],
      subscription.details?.selectedAddOns
    );
    const changesAddonAttributes = getReachabilityAddonAttributes(values);
    const payload = getReachabilityWithMobilePayload([], [], ChangesAddOns, subscription, changesAddonAttributes);

    try {
      await changeSubscriptionFeatures(payload, subscription.subscriptionId, mdmId);
      setSaving(false);
      setEditing(false);
      pushAnalyticsEvent(ChangesAddOns, EcommerceEventTypeV4.PURCHASE, analyticsBundleId);
      addNotification(t.T29V('Change order successful'), 'success');
      navigate(
        generatePath(paths.PS_MOBILE_SUBSCRIPTION, { subscriptionId: getSubscriptionUrlId(subscription) }) + search
      );
      // Refresh /pending-subscription-actions data to update subscription status
      revalidate();
    } catch (error) {
      addNotification(t.JDTO(somethingWentWrongMsg), 'error');
      setSaving(false);
      setEditing(false);
    }
  };

  return isEditing ? (
    <div>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onConfirm)} noValidate>
          <ReachabilityWithMobileEdit setEditing={setEditing} isSaving={isSaving} />
        </form>
      </FormProvider>
    </div>
  ) : (
    <div>
      <ReachabilityWithMobileView addonAttributes={addonAttributes} onEdit={onEdit} />
    </div>
  );
};
